/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited.
 */

import axios from 'axios';
import { invalidateAntiBotTokenCache } from './recaptcha';
import { startChallenge } from './antibot-challenge-stash';
import { withSolveDeadline } from './antibot-challenge-deadline';

/**
 * Returns true when the error is a 403 caused by an expired or invalid
 * anti-bot token. The backend sets title = "AntiBotInvalidTokenError" and
 * type contains "AntiBotInvalidTokenError" in the RFC-7807 problem body.
 */
export function isAntiBotError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 403) return false;
  const data = error.response?.data as Record<string, unknown> | undefined;
  return (
    data?.title === 'AntiBotInvalidTokenError' ||
    (typeof data?.type === 'string' && data.type.includes('AntiBotInvalidTokenError'))
  );
}

/**
 * Detects an anti-bot step-up challenge in an RFC-7807 error body.
 *
 * @param data The parsed RFC-7807 response body, if any.
 * @returns `true` when the body identifies an `AntiBotChallengeRequired` error.
 */
function hasChallengeTitle(data: Record<string, unknown> | undefined): boolean {
  if (!data) return false;
  if (data.title === 'AntiBotChallengeRequired') return true;
  return typeof data.type === 'string' && data.type.includes('AntiBotChallengeRequired');
}

/**
 * Returns true when the backend requires a visible reCAPTCHA challenge.
 *
 * Contract status is 428. Legacy unhandled handlers returned 500 with the
 * same title — both open the challenge overlay until all environments emit 428.
 */
export function isAntiBotChallengeError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status !== 428 && status !== 500) return false;
  return hasChallengeTitle(error.response?.data as Record<string, unknown> | undefined);
}

/**
 * Reads a string field off a 428's RFC-7807 body, or '' when absent/not a string.
 */
function readChallengeBodyField(error: unknown, field: string): string {
  if (!axios.isAxiosError(error)) return '';
  const data = error.response?.data as Record<string, unknown> | undefined;
  const value = data?.[field];
  return typeof value === 'string' ? value : '';
}

/**
 * Reads the CHECKBOX site key the backend minted for this 428, from the
 * RFC-7807 body's `challenge_site_key` field (D11).
 */
function extractChallengeSiteKey(error: unknown): string {
  return readChallengeBodyField(error, 'challenge_site_key');
}

/**
 * Reads the optional Fase 6 HMAC ticket from the 428 body's `challenge_ticket`
 * field. Absence (older backends) must never change behavior.
 */
function extractChallengeTicket(error: unknown): string | null {
  return readChallengeBodyField(error, 'challenge_ticket') || null;
}

/**
 * Opens the global anti-bot challenge overlay so the user can solve the
 * verification, and starts (or joins) the pending challenge in the stash.
 *
 * Bounded by {@link withSolveDeadline}: without it, a 428 on a shell that never
 * mounts the overlay (or any other stuck-forever case) left this await pending
 * indefinitely -- a spinner with nothing behind it, forever.
 *
 * @param error The challenge error to surface to the overlay.
 * @returns A promise resolving with the solved token, matching {@link startChallenge}.
 */
async function openChallengeOverlay(error: unknown): Promise<string> {
  const siteKey = extractChallengeSiteKey(error);
  const ticket = extractChallengeTicket(error);
  const challengeSettled = startChallenge(siteKey, ticket);
  const { reportAntiBotError } = await import('@/context/AntiBotContext');
  reportAntiBotError(error, 428, siteKey);
  await withSolveDeadline(challengeSettled);
  return challengeSettled;
}

/**
 * Builds anti-bot request headers. Pass `{ reload: true }` to force a full
 * reCAPTCHA SDK reload so a spent single-use token is not minted again, or
 * `{ challenge: true }` to attach the just-solved `X-Anti-Bot-Challenge`
 * token alongside a freshly minted `X-Anti-Bot` (D1).
 */
export type AntiBotHeadersFactory = (
  opts?: { reload?: boolean; challenge?: true },
) => Promise<Record<string, string>>;

/**
 * Executes an axios call that requires anti-bot headers.
 *
 * On a 403 AntiBotInvalidTokenError the cached token is invalidated and the
 * request is retried exactly once with headers minted after an SDK reload
 * (`getHeaders({ reload: true })`), matching the OpenAPI client recovery path.
 *
 * On 428 (or legacy 500) AntiBotChallengeRequired the challenge overlay is
 * opened; once the user solves it, the request is retried exactly once with
 * `getHeaders({ challenge: true })` (D1: a fresh X-Anti-Bot alongside the
 * solved X-Anti-Bot-Challenge). Rejects with AntiBotChallengeCancelledError,
 * unmodified, if the user cancels instead.
 *
 * Usage:
 *   const res = await withAntiBotRetry(
 *     (headers) => axios.post(url, body, { headers }),
 *     buildHeaders,
 *   );
 *
 * @param fn         Function that receives headers and performs the request.
 * @param getHeaders Async function that builds the request headers including
 *                   a fresh X-Anti-Bot token.
 */
export async function withAntiBotRetry<T>(
  fn: (headers: Record<string, string>) => Promise<T>,
  getHeaders: AntiBotHeadersFactory,
): Promise<T> {
  const headers = await getHeaders();
  try {
    return await fn(headers);
  } catch (error) {
    if (isAntiBotChallengeError(error)) {
      await openChallengeOverlay(error);
      const freshHeaders = await getHeaders({ challenge: true });
      return fn(freshHeaders);
    }
    if (isAntiBotError(error)) {
      invalidateAntiBotTokenCache();
      const freshHeaders = await getHeaders({ reload: true });
      return fn(freshHeaders);
    }
    throw error;
  }
}
